import React from 'react';
import { INETWORK_CONFIGURES } from '@api/emp/types';

type NetworkConfiguration = INETWORK_CONFIGURES['configuration'];
type RuleField = 'request_header' | 'request_payload' | 'response_header' | 'response_body';

/** Returns the display label for a rule field without using dynamic bracket access. */
const getFieldLabel = (field: RuleField): string => {
  if (field === 'request_header') return 'Request Header';
  if (field === 'request_payload') return 'Request Body';
  if (field === 'response_header') return 'Response Header';
  return 'Response Body';
};

const operatorStyle: React.CSSProperties = { color: '#52c41a', fontWeight: 'bold' };

const StyledOp: React.FC<{ children: string }> = ({ children }) => (
  <span style={operatorStyle}>{children}</span>
);

const formatValues = (values: string[]): string => values.map((v) => `'${v}'`).join(', ');

/** Returns the string[] for a rule field without using dynamic bracket access.
 *  Uses nullish-coalescing so the caller never receives undefined even when the
 *  API response omits a field that the TypeScript type marks as required. */
const getRuleValues = (conf: NetworkConfiguration | undefined, field: RuleField): string[] => {
  if (!conf) return [];
  if (field === 'request_header') return conf.request_header ?? [];
  if (field === 'request_payload') return conf.request_payload ?? [];
  if (field === 'response_header') return conf.response_header ?? [];
  return conf.response_body ?? [];
};

interface NetworkConfigSummaryProps {
  config: INETWORK_CONFIGURES;
}

/**
 * Renders a human-readable summary of a network request configuration rule
 * as a React element tree, avoiding raw-HTML string injection entirely.
 */
export const NetworkConfigSummary: React.FC<NetworkConfigSummaryProps> = ({ config }) => {
  const parts: React.ReactNode[] = [];

  const targetUrls = (config.configuration?.target_url ?? []).filter((v) => v.trim().length > 0);
  if (targetUrls.length > 0) {
    parts.push(
      <React.Fragment key="target_url">
        target URL <StyledOp>contains</StyledOp> {formatValues(targetUrls)}
      </React.Fragment>,
    );
  }

  const ruleFields: RuleField[] = [
    'request_header',
    'request_payload',
    'response_header',
    'response_body',
  ];

  for (const field of ruleFields) {
    const values = getRuleValues(config.configuration, field).filter((v) => v.trim().length > 0);
    if (values.length > 0) {
      parts.push(
        <React.Fragment key={field}>
          {getFieldLabel(field)} <StyledOp>equals</StyledOp> {formatValues(values)}
        </React.Fragment>,
      );
    }
  }

  if (parts.length === 0) {
    return <strong>Capture</strong>;
  }

  const withSeparators = parts.reduce<React.ReactNode[]>((acc, part, i) => {
    if (i > 0) {
      acc.push(
        <React.Fragment key={`sep-${i}`}>
          {' '}
          <StyledOp>AND</StyledOp>{' '}
        </React.Fragment>,
      );
    }
    acc.push(part);
    return acc;
  }, []);

  return (
    <span>
      <strong>Capture</strong> if {withSeparators}
    </span>
  );
};
